import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button, Descriptions, Result, Tag, Typography } from 'antd';
import { getUserProfile, isAuthenticated } from '@/services/api/axios.config';
import { getRequiredPermission } from '@/constants/routePermissions';
import { HOME_ROUTE } from '@/config/routes';
import { can, explainDecision, fromProfile, type PermissionSnapshot } from '@/lib/rbac';
import { useFeature } from '@/features/flags';
import { useRoles } from '@/components/system/useSystemGovernance';
import { permissionLabelKey } from '@/components/system/PermissionMatrixGrid';
import type { User } from '@/models/user';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

/**
 * The structural profile shape the route guard reads — both the auth `User`
 * (singular `role`, flat `permissions[]`) and a resolver-stamped governance
 * profile are assignable. `permissions` is optional on purpose: the stored
 * profile can predate stamping (the exact latent NPE this slice removes).
 */
export type RouteGuardProfile = (PermissionSnapshot & { role?: string }) | null | undefined;

/* eslint-disable react-refresh/only-export-components -- pure guard predicate
 * colocated with its only consumer so the S1.2 route-guard unit test exercises
 * the exact expression the component renders with (house precedent:
 * FeatureFlagContext hooks). */
/**
 * Pure Axis-A route-guard predicate (plan §Slice 1.2 — the collapsed admin
 * bypass). Zero-I/O; the component supplies profile + flag.
 *
 * - rbacV2 ON — `can(profile, required)`: a pure membership test over the
 *   resolver-stamped snapshot. NO admin special-case: superadmin is a ROLE
 *   holding `[...ALL_PERMISSIONS]` (stamped by the MSW layer), not a code
 *   bypass, so admin behaviour is unchanged by construction.
 * - rbacV2 OFF — byte-identical to the legacy check with the crash removed:
 *   `profile?.role === 'admin' || (profile?.permissions ?? []).includes(required)`.
 *   The legacy form (`!profile?.permissions.includes(required)`) threw when a
 *   stored profile carried no `permissions`; both paths are now null-safe
 *   (ON via `can`'s internal guard, OFF via `?? []`).
 */
export function isRouteAllowed(
  profile: RouteGuardProfile,
  required: string | undefined,
  rbacV2Enabled: boolean
): boolean {
  if (!required) return true;
  if (rbacV2Enabled) return can(profile, required);
  return profile?.role === 'admin' || (profile?.permissions ?? []).includes(required);
}
/* eslint-enable react-refresh/only-export-components */

/** Human `governance.permissions.*` label for a permission code — never the raw dotted code in UI. */
function usePermissionLabel(): (code: string) => string {
  const { t } = useTranslation('governance');
  return (code: string) => t(`permissions.${permissionLabelKey(code)}`);
}

interface WhyDeniedFactsProps {
  profile: User | null;
  required: string;
}

/**
 * The WHY-403 explainer body (plan §Slice 3.2) — a minimal GCP-Policy-
 * Troubleshooter-style fact list, THREE facts only:
 *
 *   1. the required permission — its human `governance.permissions.*` label,
 *      never the raw dotted code;
 *   2. the roles the user holds;
 *   3. which role(s) would grant the permission (`explainDecision`'s
 *      `grantingRoleIds` remediation hint).
 *
 * Deliberately DROPPED (ux-lens, affordance-not-status): the one-click
 * request/grant action (a dead-end — there is no request surface to route to)
 * and the "Check access" box (Slice 3.1's effective-permission preview covers
 * it). The route guard is a pure Axis-A check, so `explainDecision` runs with
 * no target row (`orgId` omitted ⇒ Axis B not evaluated) over the live role
 * catalog — the same read (`useRoles`) and the same pure resolver the
 * governance surfaces consume. Degrades gracefully: while the catalog loads
 * (or on fetch error) only the summary sub-title renders — never a spinner or
 * a raw code.
 */
function WhyDeniedFacts({ profile, required }: WhyDeniedFactsProps) {
  const { t } = useTranslation('governance');
  const permissionLabel = usePermissionLabel();
  const { roles, loading, error } = useRoles();

  const decision = useMemo(
    () =>
      roles
        ? explainDecision({ identity: fromProfile(profile), roles, orgs: [] }, required)
        : null,
    [roles, profile, required]
  );

  if (loading || error || !roles || !decision) return null;

  const roleName = (id: string) => roles.find((role) => role.id === id)?.name ?? id;

  return (
    <Descriptions column={1} size="small" style={{ maxWidth: 480, margin: '0 auto' }}>
      <Descriptions.Item label={t('access.why.requiredPermission')}>
        <Text strong>{permissionLabel(required)}</Text>
      </Descriptions.Item>
      <Descriptions.Item label={t('access.why.rolesHeld')}>
        {decision.heldRoleIds.length > 0
          ? decision.heldRoleIds.map((id) => <Tag key={id}>{roleName(id)}</Tag>)
          : '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('access.why.grantedBy')}>
        {decision.grantingRoleIds.length > 0 ? (
          decision.grantingRoleIds.map((id) => <Tag key={id}>{roleName(id)}</Tag>)
        ) : (
          <Text type="secondary">{t('access.why.noGrantingRole')}</Text>
        )}
      </Descriptions.Item>
    </Descriptions>
  );
}

interface ProtectedRouteProps {
  children?: ReactNode;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'governance']);
  const rbacV2 = useFeature('governance.rbacV2');
  const permissionLabel = usePermissionLabel();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const profile = getUserProfile() as User | null;
  // Route → permission map is data-driven from the S10.1 role-permission matrix
  // (src/constants/routePermissions.ts). Under rbacV2 a user's effective
  // permissions are the resolver-stamped snapshot (role-DAG closure + implication
  // expansion); flag-OFF they remain today's flat union with the admin role short-
  // circuit. Either way the guard itself is a pure Axis-A check (isRouteAllowed).
  const required = getRequiredPermission(location.pathname);
  if (required && !isRouteAllowed(profile, required, rbacV2)) {
    return (
      <Result
        status="403"
        title={t('governance:access.deniedTitle')}
        subTitle={
          rbacV2
            ? t('governance:access.why.summary', { permission: permissionLabel(required) })
            : t('common:messages.permissionDenied')
        }
        extra={
          <Button type="primary" onClick={() => navigate(HOME_ROUTE)}>
            {t('governance:access.back')}
          </Button>
        }
      >
        {rbacV2 && <WhyDeniedFacts profile={profile} required={required} />}
      </Result>
    );
  }

  return children ? <>{children}</> : <Outlet />;
}
